import { cn_niche, cn_stairs, cn_wall } from '@acenv/cnmap-editor';

// Patch runtime de bugs cnmap-editor (jamais de modification du code source de la librairie) : à appeler une
// fois au démarrage de l'app, avant tout usage de cnmap-editor. À retirer dès qu'une version de cnmap-editor
// corrigeant ces bugs est disponible.
export function patchCnmapEditor(): void {
    patchNicheSerializeMissingSubstrates();
    patchWallUpdateNichesDuplication();
    patchNicheComputeOffsetMissingGuard();
    patchStairsSimilarFacingsComputedOnLoad();
}

// cn_niche.serialize() (cn_niche.js) initialise `json.facings = [{}, {}]` mais oublie l'équivalent pour
// `json.substrates` avant d'y écrire un rôle défini (même à null) : "TypeError: can't access property 0,
// e.substrates is undefined" dès qu'un substrat de niche est renseigné. cn_opening.serialize() (cn_opening.js),
// non buggé, construit facings ET substrates via cn_facing_group.serialize(...) ; on réimplémente cn_niche.serialize()
// à l'identique (cf. cn_niche.js) en ajoutant l'initialisation manquante.
function patchNicheSerializeMissingSubstrates(): void {
    cn_niche.prototype.serialize = function (this: cn_niche) {
        const json = this.serialize_element();
        json.wall_position = this.wall_position;
        json.wall_side = this.wall_side;
        json.width = this.width;
        json.height = this.height;
        json.depth = this.depth;
        json.z = this.z;
        json.element_type = this.element_type.ID;
        json.facings = [{}, {}];
        json.substrates = [{}, {}]; // <- ligne manquante dans cn_niche.js d'origine
        this.get_facing_groups().forEach((fg: { side: number; roles: { code: string }[] }) => {
            fg.roles.forEach((role) => {
                const facing = this.facings[fg.side][role.code];
                if (typeof facing !== 'undefined') json.facings[fg.side][role.code] = facing ? facing.ID : null;
                const substrate = this.substrates[fg.side][role.code];
                if (typeof substrate !== 'undefined') json.substrates[fg.side][role.code] = substrate ? substrate.ID : null;
            });
        });
        // s_index existe à l'exécution (cf. cn_niche.js) mais n'est pas déclaré dans les typings cnmap-editor.
        json.wall_lock_vertex = this.wall_lock_vertex ? (this.wall_lock_vertex as unknown as { s_index: number }).s_index : -1;
        return json;
    };
}

// cn_wall.update_niches() (cn_wall.js), appelée à chaque recalcul de la géométrie du mur (donc potentiellement
// plusieurs fois par session), rattache chaque niche valide à son/ses wall_interface(s) via `wi.niches.push(niche)`
// SANS jamais vider `wi.niches` avant : chaque recalcul empile une copie de plus de la même niche, indéfiniment
// (observé : 5 entrées identiques après quelques cycles de test). On ne peut pas modifier cn_wall.js, donc on
// entoure l'appel original (dont la logique de positionnement/overlap n'est pas modifiée) d'une remise à zéro de
// wall_interface.niches pour chaque wall_interface du mur avant qu'il ne les repeuple.
function patchWallUpdateNichesDuplication(): void {
    const originalUpdateNiches = cn_wall.prototype.update_niches;
    cn_wall.prototype.update_niches = function (this: cn_wall, ...args: unknown[]) {
        this.wall_interfaces.forEach((wallInterface) => {
            wallInterface.niches = [];
        });
        return originalUpdateNiches.apply(this, args);
    };
}

// cn_niche.compute_offset() (cn_niche.js) accède à wall_interface.spaces[wall_side] sans vérifier qu'il est déjà
// renseigné - seul wall_interface lui-même est vérifié ("TypeError: can't access property 'outside',
// this.wall_interface.spaces[this.wall_side] is null"). cn_opening.compute_offset() (cn_opening.js) a le garde-fou
// manquant ici ; on l'ajoute à l'identique, sans toucher au reste de la méthode.
function patchNicheComputeOffsetMissingGuard(): void {
    cn_niche.prototype.compute_offset = function (this: cn_niche) {
        if (!this.wall) return 0;
        if (!this.wall_interface || !this.wall_interface.spaces[this.wall_side]) return 0;
        if (this.wall_interface.spaces[this.wall_side].outside) return 0;
        return this.wall_interface.spaces[this.wall_side].slab_offset;
    };
}

// cn_stairs.similar_facings (cn_stairs.js) contrôle si le pastille/popup d'édition de revêtement de l'escalier lit/écrit
// facings[0] pour tous les composants (true, valeur par défaut posée par le constructeur) ou facings[index] par composant
// (false) - cf. cn_stairs.get_facings_codes() et cn_pastille_facing.js. Ni cn_stairs.serialize() ni cn_stairs.unserialize()
// ne lisent/écrivent similar_facings : la valeur ne survit donc jamais à un rechargement du plan, quoi qu'ait fait
// l'utilisateur dans cnmap-editor - elle retombe systématiquement à true (valeur du constructeur). Résultat pour Wizy :
// SyncBienPlanService.updateCouchesStairs écrit bien stair.facings[index] pour le composant ciblé, mais au prochain
// chargement du plan similar_facings vaut à nouveau true, donc l'éditeur cnmap-editor n'affiche/n'édite jamais que
// facings[0] - le composant réellement modifié apparaît sans revêtement sélectionné. Plutôt que de persister le
// booléen (jamais fait par la librairie, donc probablement pas l'intention), on le recalcule à chaque chargement à
// partir de facings lui-même, seule donnée réellement persistée : similaire si tous les composants partagent le même
// revêtement (ou sont tous vides), comme le ferait cnmap-editor s'il gérait lui-même la cohérence de ce flag.
function patchStairsSimilarFacingsComputedOnLoad(): void {
    const originalUnserialize = cn_stairs.unserialize;
    cn_stairs.unserialize = function (json: Record<string, unknown>, scene: unknown) {
        const stairs = originalUnserialize(json, scene);
        if (stairs) stairs.similar_facings = stairs.facings.every((facing) => facing === stairs.facings[0]);
        return stairs;
    };
}
